import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DogEarCard } from '@audiq/ui';
import {
  type FlowQuestion,
  type FlowWithRelations,
  type DataType,
  type FlowQuestionUpdate,
} from '@audiq/shared';
import { api } from '../../lib/api';

interface Props {
  flow: FlowWithRelations;
}

const DATA_TYPES: { value: DataType; label: string }[] = [
  { value: 'text', label: 'Testo libero' },
  { value: 'number', label: 'Numero' },
  { value: 'choice', label: 'Scelta singola' },
  { value: 'multi_choice', label: 'Scelta multipla' },
  { value: 'date', label: 'Data' },
  { value: 'scale', label: 'Scala (es. 1-10)' },
  { value: 'boolean', label: 'Sì/No' },
];

export function QuestionsTab({ flow }: Props) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const questions = flow.questions;

  const createMut = useMutation({
    mutationFn: () =>
      api.createQuestion(flow.id, {
        sort_order: questions.length,
        objective: 'Nuova domanda — descrivi cosa vuoi capire',
        ask_examples: [],
        data_type: 'text',
        data_semantic: null,
        choices: null,
        validation: null,
        tags: [],
        flags_to_trigger: [],
        follow_up_if_vague: null,
        required: true,
        skip_if: null,
      }),
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ['flow', flow.id] });
      setExpanded((s) => new Set(s).add(q.id));
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ qid, data }: { qid: string; data: FlowQuestionUpdate }) =>
      api.updateQuestion(flow.id, qid, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  const deleteMut = useMutation({
    mutationFn: (qid: string) => api.deleteQuestion(flow.id, qid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  const reorderMut = useMutation({
    mutationFn: (orderedIds: string[]) => api.reorderQuestions(flow.id, orderedIds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  function moveQuestion(idx: number, direction: -1 | 1) {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= questions.length) return;
    const next = [...questions];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved!);
    reorderMut.mutate(next.map((q) => q.id));
  }

  // dnd-kit sensors: PointerSensor con activationConstraint (5px) per evitare
  // drag accidentale al click su pulsanti dentro la card. KeyboardSensor per
  // accessibilità (Space=grab, frecce=move, Space=drop).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(questions, oldIndex, newIndex);
    reorderMut.mutate(reordered.map((q) => q.id));
  }

  function toggleExpanded(qid: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(qid)) next.delete(qid);
      else next.add(qid);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
          Domande del flow{' '}
          <span className="mono ms-2" style={{ color: 'var(--mute)' }}>
            {questions.length}
          </span>
        </h3>
        <button
          type="button"
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className="rounded-md px-3 py-1.5 text-sm"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          + Aggiungi domanda
        </button>
      </div>

      {questions.length === 0 && (
        <DogEarCard className="rounded-md p-6 text-center">
          <p className="text-sm" style={{ color: 'var(--mute)' }}>
            Ancora nessuna domanda. Ogni domanda è un <em>objective</em> (cosa capire), non un testo
            fisso — l'LLM la formula al momento.
          </p>
        </DogEarCard>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
          <ol className="space-y-3">
            {questions.map((q, idx) => (
              <SortableQuestionItem key={q.id} id={q.id}>
                <QuestionCard
                  question={q}
                  index={idx}
                  total={questions.length}
                  expanded={expanded.has(q.id)}
                  onToggle={() => toggleExpanded(q.id)}
                  onMove={(dir) => moveQuestion(idx, dir)}
                  onDelete={() => {
                    if (confirm('Eliminare questa domanda?')) deleteMut.mutate(q.id);
                  }}
                  onSave={(data) => updateMut.mutate({ qid: q.id, data })}
                  saving={updateMut.isPending}
                />
              </SortableQuestionItem>
            ))}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// Wrapper sortable: applica transform/transition a un <li>. La handle vera è
// dentro QuestionCard (l'indicatore ⋮⋮) tramite ref + listeners passati via context.
function SortableQuestionItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute z-10 flex h-8 w-6 cursor-grab items-center justify-center text-lg select-none active:cursor-grabbing"
        style={{
          insetInlineStart: -4,
          insetBlockStart: 8,
          color: 'var(--mute)',
        }}
        aria-label="Trascina per riordinare"
        title="Trascina per riordinare"
      >
        ⋮⋮
      </div>
      {children}
    </li>
  );
}

interface QuestionCardProps {
  question: FlowQuestion;
  index: number;
  total: number;
  expanded: boolean;
  onToggle: () => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  onSave: (data: FlowQuestionUpdate) => void;
  saving: boolean;
}

function QuestionCard({
  question,
  index,
  total,
  expanded,
  onToggle,
  onMove,
  onDelete,
  onSave,
  saving,
}: QuestionCardProps) {
  const [draft, setDraft] = useState<FlowQuestionUpdate>({
    objective: question.objective,
    ask_examples: question.ask_examples,
    data_type: question.data_type,
    data_semantic: question.data_semantic,
    tags: question.tags,
    follow_up_if_vague: question.follow_up_if_vague,
    required: question.required,
  });

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({
      objective: question.objective,
      ask_examples: question.ask_examples,
      data_type: question.data_type,
      data_semantic: question.data_semantic,
      tags: question.tags,
      follow_up_if_vague: question.follow_up_if_vague,
      required: question.required,
    });

  return (
    <DogEarCard className="rounded-md">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-1 items-center gap-3">
          <span className="mono shrink-0" style={{ color: 'var(--mute)', minWidth: '1.5rem' }}>
            {String(index + 1).padStart(2, '0')}
          </span>
          <button
            type="button"
            onClick={onToggle}
            className="flex-1 text-start text-sm hover:underline"
            style={{ color: 'var(--ink)' }}
          >
            {question.objective || '(senza objective)'}
          </button>
          <span className="mono" style={{ color: 'var(--mute)' }}>
            {question.data_type}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="px-2 py-1 disabled:opacity-30"
            style={{ color: 'var(--mute)' }}
            aria-label="Sposta su"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="px-2 py-1 disabled:opacity-30"
            style={{ color: 'var(--mute)' }}
            aria-label="Sposta giù"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="px-2 py-1"
            style={{ color: 'var(--danger)' }}
          >
            ✕
          </button>
        </div>
      </header>

      {expanded && (
        <div className="space-y-3 px-4 pb-4 pt-2" style={{ borderTop: '1px solid var(--line)' }}>
          <div>
            <label className="mb-1 block text-sm font-medium">Objective</label>
            <textarea
              value={draft.objective ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, objective: e.target.value }))}
              rows={2}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              placeholder="Cosa vuoi capire da questa domanda?"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Tipo dato</label>
              <select
                value={draft.data_type}
                onChange={(e) => setDraft((d) => ({ ...d, data_type: e.target.value as DataType }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              >
                {DATA_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">
                Semantica{' '}
                <span className="mono" style={{ color: 'var(--mute)' }}>
                  (opz.)
                </span>
              </label>
              <input
                type="text"
                value={draft.data_semantic ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    data_semantic: e.target.value || null,
                  }))
                }
                placeholder="es. anatomical_location, language_level"
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Esempi di formulazione{' '}
              <span className="mono" style={{ color: 'var(--mute)' }}>
                (uno per riga)
              </span>
            </label>
            <textarea
              value={(draft.ask_examples ?? []).join('\n')}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  ask_examples: e.target.value
                    .split('\n')
                    .map((l) => l.trim())
                    .filter(Boolean),
                }))
              }
              rows={3}
              className="w-full rounded-md border px-3 py-2 font-mono text-xs"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              placeholder="Da quanto tempo lo senti?\nDa quanti giorni succede?"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Follow-up se vago{' '}
              <span className="mono" style={{ color: 'var(--mute)' }}>
                (opz.)
              </span>
            </label>
            <input
              type="text"
              value={draft.follow_up_if_vague ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  follow_up_if_vague: e.target.value || null,
                }))
              }
              placeholder="es. Sai dirmi un numero approssimativo di giorni?"
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              Tag{' '}
              <span className="mono" style={{ color: 'var(--mute)' }}>
                (separati da virgola)
              </span>
            </label>
            <input
              type="text"
              value={(draft.tags ?? []).join(', ')}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  tags: e.target.value
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean),
                }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
            />
          </div>

          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
            <input
              type="checkbox"
              checked={draft.required ?? true}
              onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
            />
            Domanda obbligatoria (no skip)
          </label>

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={() => onSave(draft)}
              disabled={!dirty || saving}
              className="rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
              style={{ background: 'var(--primary)', color: '#fff' }}
            >
              {saving ? 'Salvo…' : 'Salva domanda'}
            </button>
          </div>
        </div>
      )}
    </DogEarCard>
  );
}
